import clsx from 'clsx';
import {
  Calculator,
  Home,
  LayoutDashboard,
  LayoutGrid,
  Maximize2,
  MonitorPlay,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isTasFlow } from '../constants';
import { useStore } from '../store';
import { showToast } from '../toast';

interface CommandOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  group: 'Navigation' | 'Actions' | 'Context';
  keywords?: string[];
}

export function CommandPalette() {
  const {
    mode,
    operator,
    isAdmin,
    showDashboard,
    showUserSelection,
    showOperatorDashboard,
    showEpisodesManagement,
    showAdminAnalytics,
    backToEpisodeList,
    lookupEpisode,
    isZenMode,
    toggleZenMode,
    invalidateEpisodeSortCache,
  } = useStore();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Toggle handlers (mocking state access where needed or using direct store actions)
  // Note: Some toggle actions like Zen Mode are local to Viewer.
  // For global commands, we focus on navigation and global state.

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const options: CommandOption[] = useMemo(() => {
    const opts: CommandOption[] = [];

    // Navigation
    if (isAdmin) {
      opts.push({
        id: 'nav-dashboard',
        label: 'Go to Admin Overview',
        group: 'Navigation',
        icon: <LayoutDashboard className="h-4 w-4" />,
        action: () => void showDashboard(),
      });
      opts.push({
        id: 'nav-episodes',
        label: 'Manage Episodes',
        group: 'Navigation',
        icon: <LayoutGrid className="h-4 w-4" />,
        action: () => showEpisodesManagement(),
      });
      opts.push({
        id: 'nav-analytics',
        label: 'View Leaderboard',
        group: 'Navigation',
        icon: <Calculator className="h-4 w-4" />,
        action: () => showAdminAnalytics(),
      });
    }

    if (operator) {
      opts.push({
        id: 'nav-op-dashboard',
        label: 'My Dashboard',
        group: 'Navigation',
        icon: <Home className="h-4 w-4" />,
        action: () => void showOperatorDashboard(),
      });
    }

    opts.push({
      id: 'nav-switch-user',
      label: 'Switch User / Login',
      group: 'Navigation',
      icon: <Users className="h-4 w-4" />,
      action: () => void showUserSelection(),
    });

    // Context Actions
    if (mode === 'viewer') {
      opts.push({
        id: 'ctx-zen',
        label: isZenMode ? 'Exit Zen Mode' : 'Enter Zen Mode',
        group: 'Actions',
        icon: <Maximize2 className="h-4 w-4" />,
        action: () => toggleZenMode(),
      });
      opts.push({
        id: 'ctx-back',
        label: 'Back to List',
        group: 'Context',
        icon: <ArrowLeftIcon />,
        action: () => void backToEpisodeList(),
      });
    }

    opts.push({
      id: 'act-clear-cache',
      label: 'Clear Local Cache',
      group: 'Actions',
      icon: <RefreshCw className="h-4 w-4" />,
      action: () => {
        invalidateEpisodeSortCache();
        showToast('Cache cleared');
      },
    });

    // Search Action (Dynamic)
    if (query.length > 5 && /^[a-zA-Z0-9-]+$/.test(query)) {
      opts.push({
        id: 'search-episode',
        label: `Open Episode: ${query}`,
        group: 'Actions',
        icon: <MonitorPlay className="h-4 w-4" />,
        action: () => void lookupEpisode(query, { autoOpen: true }),
      });
    }

    return opts;
  }, [
    isAdmin,
    operator,
    mode,
    query,
    isZenMode,
    showDashboard,
    showEpisodesManagement,
    showAdminAnalytics,
    showOperatorDashboard,
    showUserSelection,
    backToEpisodeList,
    lookupEpisode,
    toggleZenMode,
    invalidateEpisodeSortCache,
  ]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const lowerQuery = query.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lowerQuery) ||
        opt.group.toLowerCase().includes(lowerQuery) ||
        opt.id === 'search-episode' // Always show dynamic search if generated
    );
  }, [query, options]);

  const handleSelect = (option: CommandOption) => {
    option.action();
    setIsOpen(false);
  };

  const handleKeyDownList = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions[selectedIndex]) {
        handleSelect(filteredOptions[selectedIndex]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm pt-[20vh] animate-in fade-in duration-100">
      <div
        role="dialog"
        tabIndex={-1}
        className="w-full max-w-lg overflow-hidden rounded-xl border border-brand-border bg-brand-surface shadow-2xl animate-in zoom-in-95 duration-100"
        onKeyDown={handleKeyDownList}
      >
        <div className="flex items-center border-b border-brand-border px-4 py-3">
          <Search className="h-5 w-5 text-brand-text-secondary" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent px-3 text-sm text-brand-text-primary placeholder:text-brand-text-secondary/50 focus:outline-none"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <kbd className="hidden sm:inline-block rounded border border-brand-border bg-brand-dark px-1.5 py-0.5 text-[10px] font-bold text-brand-text-secondary">
              ESC
            </kbd>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-brand-text-secondary hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-brand-text-secondary">
              No results found.
            </div>
          ) : (
            <div className="space-y-1 px-2">
              {filteredOptions.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={clsx(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    index === selectedIndex
                      ? 'bg-brand-orange text-white'
                      : 'text-brand-text-primary hover:bg-brand-surface-highlight'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center gap-3">
                    {option.icon}
                    <span>{option.label}</span>
                  </div>
                  {index === selectedIndex && (
                    <span className="text-[10px] font-bold opacity-80">↵</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-brand-surface-highlight/30 px-4 py-2 text-[10px] text-brand-text-secondary border-t border-brand-border flex justify-between">
          <span>Navigation & Actions</span>
          <span>{isTasFlow ? 'SecTas' : 'SecShot'} CLI</span>
        </div>
      </div>
    </div>
  );
}

function ArrowLeftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <title>Back</title>
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}
